import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Shield, User, Activity } from 'lucide-react';

const AuditTrailViewer = ({ reducedMotion }) => {
  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchAuditTrail();
  }, []);

  const fetchAuditTrail = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/audit/trail`);
      const data = await response.json();
      setAuditLogs(data.logs || []);
    } catch (error) {
      console.error('Failed to fetch audit trail:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    if (action.includes('delete') || action.includes('revoke')) return 'text-red-400';
    if (action.includes('create') || action.includes('grant')) return 'text-emerald-400';
    if (action.includes('update') || action.includes('modify')) return 'text-ink-500';
    return 'text-slate-400';
  };

  const getActionBadge = (action) => {
    if (action.includes('delete') || action.includes('revoke')) return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (action.includes('create') || action.includes('grant')) return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
    if (action.includes('update') || action.includes('modify')) return 'bg-ink-500 text-ink-500 border-ink-300 dark:border-ink-700';
    return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="audit-trail-viewer-card">
      <CardHeader>
        <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-400" />
          Audit Trail Viewer
        </CardTitle>
        <CardDescription className="text-slate-400 mt-1">
          Complete log of all system actions and data access
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-slate-800/30 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : auditLogs.length > 0 ? (
          <div className="space-y-2">
            {auditLogs.map((log, index) => (
              <div
                key={index}
                className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50 hover:bg-slate-800/50 transition-all duration-200"
                style={{
                  animation: reducedMotion ? 'none' : `fadeIn 0.3s ease-out ${index * 0.03}s both`
                }}
                data-testid={`audit-log-${index}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                      <Activity className="w-4 h-4 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-sm font-medium ${getActionColor(log.action)}`} data-testid="audit-action">{log.action}</span>
                        <Badge className={getActionBadge(log.action)} data-testid="audit-badge">        {log.resource}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-400" data-testid="audit-details">{log.details}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500" data-testid="audit-timestamp">{log.timestamp}</p>
                    <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                      <User className="w-3 h-3" />
                      <span data-testid="audit-user">{log.user}</span>
                    </div>
                  </div>
                </div>
                {log.ip_address && (
                  <div className="mt-2 pt-2 border-t border-slate-700/30 flex items-center gap-4 text-xs text-slate-500">
                    <span>IP: <span data-testid="audit-ip">{log.ip_address}</span></span>
                    <span>Session: <span data-testid="audit-session">{log.session_id}</span></span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Shield className="w-12 h-12 mx-auto mb-3 text-slate-600" />
            <p className="text-slate-400">No audit logs available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AuditTrailViewer;